import { CloseCircleFilled, SearchUniversal, SendUniversal, TickMarkFilled } from '@duxton-web/icons';
import { Button, Collapse, Flex, Input, Radio, Select, Table, Tag, Typography, theme } from '@duxton-web/ui';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { JourneyTestNotificationRequest } from 'omega-rtc/actions/journey-test-notification';
import { getJourneyTestRecipient, isSingleJourneyRecipient } from 'omega-rtc/utils/journey-notification-recipient';
import { resolveJourneyCsvRecords, JourneyCsvResult } from 'omega-rtc/utils/journey-notification-csv';
import { JourneyCsvTestNotification } from './JourneyCsvTestNotification';
import { getCountryFlag } from 'omega-rtc/utils/country-flag';
import { getHedwigTemplateUrl } from 'omega-rtc/utils/hedwig-template-url';
import {
  findJourneyTemplates,
  isJourneyAttributesObject,
  JourneyGroupInput,
} from 'omega-rtc/utils/journey-template-matcher';
import { formatJourneyAttributeValue } from './JourneyTemplateVariantMapping';
import './JourneyCampaignDetailSections.scss';

export { getJourneyTestRecipient, isSingleJourneyRecipient } from 'omega-rtc/utils/journey-notification-recipient';

type Props = {
  campaignKey: string;
  countryName: string;
  countryCode?: string;
  countryID?: number;
  audience?: string;
  groups: JourneyGroupInput[];
  isSynced: boolean;
  isSyncInProgress: boolean;
  isPending?: boolean;
  // eslint-disable-next-line no-unused-vars -- Function-type parameter, not a runtime variable.
  onSend: (request: JourneyTestNotificationRequest) => Promise<unknown>;
};
type MappingSnapshot = {
  attributes: Record<string, unknown>;
  result: JourneyCsvResult;
  campaignKey: string;
  mappingsKey: string;
};

const DEFAULT_JSON = `{
  "is_gu": true,
  "is_traveller": true,
  "preferred_verticals": ["food", "transport"]
}`;

export const JourneyTestNotification = ({
  campaignKey, countryName, countryCode, countryID, audience, groups, isSynced, isSyncInProgress, isPending = false, onSend,
}: Props) => {
  const { token } = theme.useToken();
  const [mode, setMode] = useState<'json' | 'csv'>('json');
  const [csvBusy, setCsvBusy] = useState(false);
  const [selectedCandidateKey, setSelectedCandidateKey] = useState<string>();
  const [jsonInput, setJsonInput] = useState(DEFAULT_JSON);
  const [recipientID, setRecipientID] = useState('');
  const [error, setError] = useState('');
  const [snapshot, setSnapshot] = useState<MappingSnapshot>();
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<'success' | 'failure'>();
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const mappingsKey = JSON.stringify(groups);
  const normalizedCountryCode = typeof countryCode === 'string' ? countryCode.trim().toUpperCase() : '';
  const displayCountryCode = /^[A-Z]{2}$/.test(normalizedCountryCode) ? normalizedCountryCode : '';
  const identityKey = `${campaignKey}:${audience}:${displayCountryCode}:${countryName}`;
  const requestContext = `${identityKey}:${mappingsKey}`;
  const currentIdentity = useRef(requestContext);
  currentIdentity.current = requestContext;
  const recipient = getJourneyTestRecipient(audience);
  const busy = sending || isPending || csvBusy;
  const ready = isSynced && !isSyncInProgress && groups.some(group => (
    group && Array.isArray(group.variants) && group.variants.length > 0
  ));
  const currentSnapshot = snapshot && snapshot.campaignKey === identityKey
    && snapshot.mappingsKey === mappingsKey ? snapshot : undefined;

  const onCsvBusyChange = useCallback((value: boolean) => {
    if (mounted.current) setCsvBusy(value);
  }, []);

  const selectedMatch = currentSnapshot && selectedCandidateKey !== undefined
    ? currentSnapshot.result.candidates[Number(selectedCandidateKey)] : undefined;
  const matchedCount = currentSnapshot ? currentSnapshot.result.candidates.length : 0;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    setSnapshot(undefined);
    setSelectedCandidateKey(undefined);
    setError('');
    setOutcome(undefined);
  }, [identityKey, mappingsKey, isSynced, isSyncInProgress]);
  useEffect(() => {
    setRecipientID('');
  }, [identityKey]);

  const clearMatch = () => {
    setSnapshot(undefined);
    setSelectedCandidateKey(undefined);
    setError('');
    setOutcome(undefined);
  };
  const findMatchedTemplate = () => {
    if (!ready || busy) return;
    clearMatch();
    let attributes: Record<string, unknown>;
    try {
      const parsed = JSON.parse(jsonInput);
      if (!isJourneyAttributesObject(parsed)) {
        setError('User attributes must be a JSON object');
        return;
      }
      attributes = parsed;
    } catch (_) {
      setError('User attributes JSON must be valid JSON');
      return;
    }
    const result = resolveJourneyCsvRecords([{ row: 1, attributes }], groups, countryID)[0];
    setSelectedCandidateKey(result.candidates.length === 1 ? '0' : undefined);
    setSnapshot({ attributes, result, campaignKey: identityKey, mappingsKey });
  };

  const sendTestNotification = async () => {
    if (inFlight.current || busy || outcome === 'success' || !ready || !recipient || !currentSnapshot
      || !selectedMatch || selectedMatch.status !== 'matched') return;
    const id = recipientID.trim();
    if (!isSingleJourneyRecipient(recipientID)) {
      setError(`Enter one ${recipient.label}; comma- or newline-separated lists are not supported.`);
      return;
    }
    const validated = findJourneyTemplates(groups, currentSnapshot.attributes).find(match => (
      match.status === 'matched' && match.groupID === selectedMatch.groupID
      && match.variantName === selectedMatch.variantName && match.templateID === selectedMatch.templateID
      && match.channel === selectedMatch.channel
    ));
    if (JSON.stringify(groups) !== currentSnapshot.mappingsKey || !validated || validated.status !== 'matched') {
      clearMatch();
      setError('Template mappings changed. Find the matched template again.');
      return;
    }
    inFlight.current = true;
    setSending(true);
    setError('');
    setOutcome(undefined);
    const sentIdentity = requestContext;
    try {
      await onSend({ recipientID: id,
        recipientType: recipient.type,
        template: { id: validated.templateID,
          enableLangDetection: true,
          extraParams: currentSnapshot.attributes } });
      if (mounted.current && currentIdentity.current === sentIdentity) setOutcome('success');
    } catch (_) {
      if (mounted.current && currentIdentity.current === sentIdentity) setOutcome('failure');
    } finally {
      inFlight.current = false;
      if (mounted.current) setSending(false);
    }
  };

  const mappings = currentSnapshot ? [{
    key: '1',
    row: 1,
    country: displayCountryCode || countryName,
    groupID: selectedMatch?.groupID || '—',
    variantName: selectedMatch?.variantName || '—',
    attributes: currentSnapshot.attributes,
    templateID: selectedMatch?.templateID || '',
    channel: selectedMatch?.channel || '',
    error: selectedMatch ? '' : currentSnapshot.result.error,
  }] : [];

  const renderTemplateResult = (mapping: typeof mappings[number]) => (
    <Flex vertical gap="6px">
      <Flex align="center" gap="6px">
        {mapping.error ? (
          <CloseCircleFilled
            aria-label="Template match failed"
            style={{ color: token.colorError, fontSize: token.fontSizeLG, flexShrink: 0 }}
          />
        ) : (
          <TickMarkFilled
            aria-label="Template matched"
            style={{ color: token.colorSuccess, fontSize: token.fontSizeLG, flexShrink: 0 }}
          />
        )}
        {mapping.error ? (
          <Typography.Text type="danger">{mapping.error}</Typography.Text>
        ) : (
          <a href={getHedwigTemplateUrl(mapping.templateID, mapping.channel)} rel="noreferrer" target="_blank">
            {mapping.templateID}
          </a>
        )}
      </Flex>
      {matchedCount > 1 && <Select
        virtual={false} aria-label="Matched template for JSON record" placeholder="Select a matched variant"
        value={selectedCandidateKey} disabled={busy}
        options={currentSnapshot!.result.candidates.map((match, index) => ({
          value: String(index), label: `${match.groupID} / ${match.variantName || match.templateID}`,
        }))}
        onChange={(value) => {
          if (busy || inFlight.current) return;
          setSelectedCandidateKey(value);
          setOutcome(undefined);
          setError('');
        }}
      />}
    </Flex>
  );

  return (
    <Collapse
      className="journey-campaign-tool-collapse journey-test-notification"
      defaultActiveKey={[]}
      items={[{
        key: 'test-notification',
        label: <Typography.Text strong>Test notification</Typography.Text>,
        children: (
          <Flex vertical gap="16px">
            <Typography.Text type="secondary">
              Resolve the Hedwig template for one user JSON object or a CSV list of
              user attributes before sending a test notification.
            </Typography.Text>
            <Radio.Group
              optionType="button" value={mode} disabled={busy}
              onChange={(event) => {
                if (busy || inFlight.current) return;
                clearMatch(); setCsvBusy(false); setMode(event.target.value);
              }} options={[
                { label: 'User JSON', value: 'json' },
                { label: 'CSV file', value: 'csv' },
              ]}
            />
            {mode === 'csv' ? (
              <JourneyCsvTestNotification
                campaignKey={campaignKey} countryName={countryName} countryCode={countryCode} countryID={countryID}
                audience={audience} groups={groups} isSynced={isSynced} isSyncInProgress={isSyncInProgress}
                isPending={isPending} onSend={onSend} onBusyChange={onCsvBusyChange}
              />
            ) : (
              <React.Fragment>
                <Input.TextArea
                  aria-label="User attributes JSON" autoSize={{ minRows: 6, maxRows: 12 }}
                  value={jsonInput} disabled={busy} data-dd-privacy="hidden"
                  onChange={(event) => { setJsonInput(event.target.value); clearMatch(); }}
                />
                {!ready && <Typography.Text type="secondary">
                  {isSyncInProgress ? 'Sheet synchronization is in progress.' : 'Synchronized template variants are required.'}
                </Typography.Text>}
                {!recipient && <Typography.Text type="danger">Unsupported campaign audience. Test notification is unavailable.</Typography.Text>}
                {error && <Typography.Text type="danger" role="alert">{error}</Typography.Text>}
                <div>
                  <Button
                    icon={<SearchUniversal aria-hidden="true" />} disabled={!ready || busy || !jsonInput.trim()}
                    onClick={findMatchedTemplate}
                  >Find Matched Template</Button>
                </div>
                {!!mappings.length && (
                  <React.Fragment>
                    <Typography.Title level={4} style={{ margin: '8px 0 0' }}>Mapped template IDs</Typography.Title>
                    <Table
                      className="journey-test-notification-table" dataSource={mappings} pagination={false}
                      rowKey="key" size="small"
                      columns={[
                        { dataIndex: 'row', key: 'row', title: 'Row', width: 64 },
                        { dataIndex: 'country',
                          key: 'country',
                          title: 'Country',
                          render: (country) => (
                            <Flex align="center" gap="6px" wrap="nowrap" title={countryName}>
                              <span role="img" aria-label={displayCountryCode ? `${countryName} flag` : 'Country flag unavailable'}>
                                {getCountryFlag(displayCountryCode)}
                              </span>
                              <span>{country}</span>
                            </Flex>
                          ) },
                        { dataIndex: 'attributes',
                          key: 'attributes',
                          title: 'User attributes',
                          render: (attributes) => (
                            <Flex gap="6px" wrap="wrap" data-dd-privacy="hidden">
                              {Object.entries(attributes).map(([key, value]) => (
                                <Tag key={key}>{key}: {formatJourneyAttributeValue(value)}</Tag>
                              ))}
                            </Flex>
                          ) },
                        { dataIndex: 'groupID',
                          key: 'groupID',
                          title: 'Group ID',
                          onCell: (mapping) => ({
                            colSpan: mapping.error ? 3 : 1,
                            title: mapping.error ? 'Template matching result' : undefined,
                          }),
                          render: (groupID, mapping) => (mapping.error ? renderTemplateResult(mapping) : groupID) },
                        { dataIndex: 'variantName',
                          key: 'variantName',
                          title: 'Variant name',
                          onCell: (mapping) => ({ colSpan: mapping.error ? 0 : 1 }) },
                        { dataIndex: 'templateID',
                          key: 'templateID',
                          title: 'Mapped template ID',
                          onCell: (mapping) => ({ colSpan: mapping.error ? 0 : 1 }),
                          render: (_, mapping) => renderTemplateResult(mapping) },
                      ]}
                    />
                    {matchedCount > 1 && (
                      <Typography.Text type="secondary">Multiple variants match this user. Select one to send a single test notification.</Typography.Text>
                    )}
                    <Flex align="end" gap="12px" wrap="wrap">
                      <Flex vertical gap="6px" style={{ flex: '1 1 320px' }}>
                        <Typography.Text strong>{recipient ? recipient.label : 'Recipient ID'}</Typography.Text>
                        <Input
                          aria-label={recipient ? recipient.label : 'Recipient ID'}
                          placeholder={`Enter ${recipient ? recipient.label : 'recipient ID'}`} value={recipientID}
                          disabled={busy || !recipient} data-dd-privacy="hidden"
                          onPaste={(event) => {
                            if (/[,\r\n]/.test(event.clipboardData.getData('text'))) {
                              event.preventDefault();
                              setError('Enter one recipient ID; comma- or newline-separated lists are not supported.');
                            }
                          }}
                          onChange={(event) => { setRecipientID(event.target.value); setError(''); setOutcome(undefined); }}
                        />
                      </Flex>
                      <Button
                        type="primary" icon={<SendUniversal aria-hidden="true" />} loading={busy}
                        disabled={busy || outcome === 'success' || !ready || !recipient || !recipientID.trim() || !selectedMatch || selectedMatch.status !== 'matched'}
                        onClick={sendTestNotification}
                      >Test notification</Button>
                    </Flex>
                  </React.Fragment>
                )}
                {sending && <Typography.Text role="status">Sending test notification…</Typography.Text>}
                {outcome === 'success' && <Typography.Text role="status" type="success">Test notification request succeeded.</Typography.Text>}
                {outcome === 'failure' && <Typography.Text role="alert" type="danger">
              The test notification request failed. Delivery may have occurred; verify before retrying.
                </Typography.Text>}
              </React.Fragment>
            )}
          </Flex>
        ),
      }]}
    />
  );
};
